import * as Immutable from 'immutable';
import moment from 'moment';
import * as React from 'react';

import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ReactTable, { Filter } from 'react-table';
import { Dropdown, Loader } from 'semantic-ui-react';

import { localeData } from '../../../locales/data';
import { alphaDetailIdSelector } from '../../../reducers/AlphasReducer/AlphasSelector';
import { initialSelectedColumnsAlphas } from '../../../reducers/AlphasReducer/AlphasReducer';
import { AlphaColumnBase } from '../../../reducers/AlphasReducer/AlphasReducer.types';
import { DatetimeFilters } from '../../../shared/data';
import { allColumnsParents } from '../../../shared/data/initialColumnsTable';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { TablePagination } from '../../Table';
import { composeGetTrProps, RowColor, RowHighlightFocus } from '../../Table/Table';
import { AlphaTableType } from '../../../types';

interface AlphasListProps {
  data: Immutable.List<any>;
  handlePageChange: (pageIndex: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  handleFilteredChange: (newFiltering: Filter[], column: any, value: any) => void;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  filters: any;
  alphaCount: number;
  alphaMessage: string;
  pageSizeLimit: number;
  modifiers: any;
  tableData: any;
  columns: any;
  type: AlphaTableType;
  tableName: string;
  handleOpenModal: () => void;
  handleDeleteList: () => void;
  isFetchingInnerCorrelation: boolean;
  superAlphaName: string | null;
  isSuperAlpha: boolean;
  isSuperSelection: boolean;
}

const TableTitle = ({ title, values, superAlpha = false, hasMenu = true, handleOpenModal, handleDeleteList }: any) => (
  <div className={addModifiers('alphas-react-table', superAlpha ? ['super-alpha'] : [])}>
    <div className="alphas-react-table__title">
      <FormattedMessageHelper id={title} values={values} />
    </div>
    {hasMenu && (
      <Dropdown
        trigger={<div className="alphas-react-table__edit--filter" />}
        className="alphas-react-table__dropdown"
        icon={null}
        pointing="top right"
      >
        <Dropdown.Menu>
          <Dropdown.Item onClick={handleOpenModal}>
            <div className="alphas-react-table__option">
              <FormattedMessageHelper
                tagName="span"
                id="alphasComparisonMain.tableTitle.renameList"
                defaultMessage={localeData.en['alphasComparisonMain.tableTitle.renameList']}
              />
            </div>
          </Dropdown.Item>
          <Dropdown.Item onClick={handleDeleteList}>
            <Link to="/alphas/lists" className="alphas-react-table__option">
              <FormattedMessageHelper
                tagName="span"
                id="alphasComparisonMain.tableTitle.deleteList"
                defaultMessage={localeData.en['alphasComparisonMain.tableTitle.deleteList']}
              />
            </Link>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    )}
  </div>
);

const convertAlphaFiltersTotableFilters = (filters: any) => {
  let allFiltered: any = [];

  if (filters) {
    Object.keys(filters).forEach((key: string) => {
      const regexFilter = new RegExp(`(${allColumnsParents.join('\\.|')}\\.)`);
      const id = key.replace(regexFilter, '');
      const filteredColumn = initialSelectedColumnsAlphas.find((alphaColumn: AlphaColumnBase) => alphaColumn.id === id);
      const operator = filteredColumn ? filteredColumn.filterOperator : null;
      const dateTimeField = id.replace(/<|>/, '');

      if (/^[\w]+<$/.test(id) === true && DatetimeFilters.includes(dateTimeField)) {
        return;
      }
      if (/^[\w]+>$/.test(id) === true && DatetimeFilters.includes(dateTimeField)) {
        const value = [moment(filters[key]).toISOString(), moment(filters[`${dateTimeField}<`]).toISOString()];
        allFiltered = allFiltered.concat({ id: dateTimeField, value });
      } else if (operator && /^[\w]+<$/.test(id) === false && !DatetimeFilters.includes(dateTimeField)) {
        let value = filters[key];
        if (value && typeof value === 'string') {
          const regex = new RegExp(operator, 'g');
          value = value.replace(regex, '');
          allFiltered = allFiltered.concat({ id, value });
        }
      } else if (/^[\w]+<$/.test(id) === false && !DatetimeFilters.includes(dateTimeField)) {
        const value = filters[key];
        allFiltered = allFiltered.concat({ id, value });
      }
    });
  }

  return allFiltered;
};

const emptyFunction = () => {
  return;
};
export const AlphasList = ({
  data,
  handlePageChange,
  handlePageSizeChange,
  currentPage,
  pageSize,
  totalPages,
  handleFilteredChange,
  filters,
  alphaCount,
  alphaMessage,
  pageSizeLimit,
  modifiers,
  tableData,
  columns,
  type,
  tableName,
  handleOpenModal,
  handleDeleteList,
  isFetchingInnerCorrelation,
  superAlphaName,
}: AlphasListProps) => {
  const alphaDetailId = useSelector(alphaDetailIdSelector);

  if (data === undefined || isFetchingInnerCorrelation) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  } else {
    return (
      <div className={`${addModifiers('alphas-list-table', modifiers)} competition__honor`}>
        {type === 'alphaTag' && (
          <TableTitle
            title={'alphas.title.list'}
            values={{ tableName }}
            handleOpenModal={handleOpenModal}
            handleDeleteList={handleDeleteList}
          />
        )}
        {superAlphaName !== null && type === 'superAlpha' && (
          <TableTitle
            title={'alphas.title.super.alpha'}
            values={{ tableName: superAlphaName }}
            handleOpenModal={handleOpenModal}
            handleDeleteList={handleDeleteList}
            superAlpha={true}
            hasMenu={false}
          />
        )}
        <ReactTable
          data={tableData}
          getTrProps={composeGetTrProps(RowColor, RowHighlightFocus(alphaDetailId))}
          columns={columns}
          filterable={true}
          PaginationComponent={TablePagination({
            hasButtons: false,
            numOfResults: alphaCount,
            compareAlphas: emptyFunction,
            removeAlphas: emptyFunction,
            pageSizeLimit: pageSizeLimit,
            additionalMessage: alphaMessage,
          })}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onFilteredChange={handleFilteredChange}
          manual={true}
          page={currentPage}
          pageSize={pageSize}
          pages={totalPages}
          filtered={convertAlphaFiltersTotableFilters(filters)}
          className="intro-step-alphas-3 -highlight-focus"
        />
      </div>
    );
  }
};
